// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { VirtualNetworkAppliances } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  VirtualNetworkAppliance,
  VirtualNetworkAppliancesListAllNextOptionalParams,
  VirtualNetworkAppliancesListAllOptionalParams,
  VirtualNetworkAppliancesListAllResponse,
  VirtualNetworkAppliancesListNextOptionalParams,
  VirtualNetworkAppliancesListOptionalParams,
  VirtualNetworkAppliancesListResponse,
  VirtualNetworkAppliancesDeleteOptionalParams,
  VirtualNetworkAppliancesDeleteResponse,
  VirtualNetworkAppliancesGetOptionalParams,
  VirtualNetworkAppliancesGetResponse,
  VirtualNetworkAppliancesCreateOrUpdateOptionalParams,
  VirtualNetworkAppliancesCreateOrUpdateResponse,
  TagsObject,
  VirtualNetworkAppliancesUpdateTagsOptionalParams,
  VirtualNetworkAppliancesUpdateTagsResponse,
  VirtualNetworkAppliancesListAllNextResponse,
  VirtualNetworkAppliancesListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing VirtualNetworkAppliances operations. */
export class VirtualNetworkAppliancesImpl implements VirtualNetworkAppliances {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class VirtualNetworkAppliances class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Gets all virtual network appliances in a subscription.
   * @param options The options parameters.
   */
  public listAll(
    options?: VirtualNetworkAppliancesListAllOptionalParams,
  ): PagedAsyncIterableIterator<VirtualNetworkAppliance> {
    const iter = this.listAllPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listAllPagingPage(options, settings);
      },
    };
  }

  private async *listAllPagingPage(
    options?: VirtualNetworkAppliancesListAllOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<VirtualNetworkAppliance[]> {
    let result: VirtualNetworkAppliancesListAllResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listAll(options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listAllNext(continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listAllPagingAll(
    options?: VirtualNetworkAppliancesListAllOptionalParams,
  ): AsyncIterableIterator<VirtualNetworkAppliance> {
    for await (const page of this.listAllPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Gets all virtual network appliances in a resource group.
   * @param resourceGroupName The name of the resource group.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    options?: VirtualNetworkAppliancesListOptionalParams,
  ): PagedAsyncIterableIterator<VirtualNetworkAppliance> {
    const iter = this.listPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    options?: VirtualNetworkAppliancesListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<VirtualNetworkAppliance[]> {
    let result: VirtualNetworkAppliancesListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(resourceGroupName, continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    options?: VirtualNetworkAppliancesListOptionalParams,
  ): AsyncIterableIterator<VirtualNetworkAppliance> {
    for await (const page of this.listPagingPage(resourceGroupName, options)) {
      yield* page;
    }
  }

  /**
   * Deletes the specified virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    options?: VirtualNetworkAppliancesDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkAppliancesDeleteResponse>,
      VirtualNetworkAppliancesDeleteResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkAppliancesDeleteResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, virtualNetworkApplianceName, options },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkAppliancesDeleteResponse,
      OperationState<VirtualNetworkAppliancesDeleteResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Deletes the specified virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    options?: VirtualNetworkAppliancesDeleteOptionalParams,
  ): Promise<VirtualNetworkAppliancesDeleteResponse> {
    const poller = await this.beginDelete(resourceGroupName, virtualNetworkApplianceName, options);
    return poller.pollUntilDone();
  }

  /**
   * Gets information about the specified virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    options?: VirtualNetworkAppliancesGetOptionalParams,
  ): Promise<VirtualNetworkAppliancesGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualNetworkApplianceName, options },
      getOperationSpec,
    );
  }

  /**
   * Creates or updates a virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param parameters Parameters supplied to the create or update virtual network appliance operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdate(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    parameters: VirtualNetworkAppliance,
    options?: VirtualNetworkAppliancesCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkAppliancesCreateOrUpdateResponse>,
      VirtualNetworkAppliancesCreateOrUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<VirtualNetworkAppliancesCreateOrUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        virtualNetworkApplianceName,
        parameters,
        options,
      },
      spec: createOrUpdateOperationSpec,
    });
    const poller = await createHttpPoller<
      VirtualNetworkAppliancesCreateOrUpdateResponse,
      OperationState<VirtualNetworkAppliancesCreateOrUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Creates or updates a virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param parameters Parameters supplied to the create or update virtual network appliance operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    parameters: VirtualNetworkAppliance,
    options?: VirtualNetworkAppliancesCreateOrUpdateOptionalParams,
  ): Promise<VirtualNetworkAppliancesCreateOrUpdateResponse> {
    const poller = await this.beginCreateOrUpdate(
      resourceGroupName,
      virtualNetworkApplianceName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Updates a virtual network appliance tags.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param parameters Parameters supplied to update virtual network appliance tags.
   * @param options The options parameters.
   */
  updateTags(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    parameters: TagsObject,
    options?: VirtualNetworkAppliancesUpdateTagsOptionalParams,
  ): Promise<VirtualNetworkAppliancesUpdateTagsResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, virtualNetworkApplianceName, parameters, options },
      updateTagsOperationSpec,
    );
  }

  /**
   * Gets all virtual network appliances in a subscription.
   * @param options The options parameters.
   */
  private _listAll(
    options?: VirtualNetworkAppliancesListAllOptionalParams,
  ): Promise<VirtualNetworkAppliancesListAllResponse> {
    return this.client.sendOperationRequest({ options }, listAllOperationSpec);
  }

  /**
   * Gets all virtual network appliances in a resource group.
   * @param resourceGroupName The name of the resource group.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    options?: VirtualNetworkAppliancesListOptionalParams,
  ): Promise<VirtualNetworkAppliancesListResponse> {
    return this.client.sendOperationRequest({ resourceGroupName, options }, listOperationSpec);
  }

  /**
   * ListAllNext
   * @param nextLink The nextLink from the previous successful call to the ListAll method.
   * @param options The options parameters.
   */
  private _listAllNext(
    nextLink: string,
    options?: VirtualNetworkAppliancesListAllNextOptionalParams,
  ): Promise<VirtualNetworkAppliancesListAllNextResponse> {
    return this.client.sendOperationRequest({ nextLink, options }, listAllNextOperationSpec);
  }

  /**
   * ListNext
   * @param resourceGroupName The name of the resource group.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    nextLink: string,
    options?: VirtualNetworkAppliancesListNextOptionalParams,
  ): Promise<VirtualNetworkAppliancesListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, nextLink, options },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkAppliances/{virtualNetworkApplianceName}",
  httpMethod: "DELETE",
  responses: {
    200: {
      headersMapper: Mappers.VirtualNetworkAppliancesDeleteHeaders,
    },
    201: {
      headersMapper: Mappers.VirtualNetworkAppliancesDeleteHeaders,
    },
    202: {
      headersMapper: Mappers.VirtualNetworkAppliancesDeleteHeaders,
    },
    204: {
      headersMapper: Mappers.VirtualNetworkAppliancesDeleteHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkApplianceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkAppliances/{virtualNetworkApplianceName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkAppliance,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkApplianceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkAppliances/{virtualNetworkApplianceName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkAppliance,
    },
    201: {
      bodyMapper: Mappers.VirtualNetworkAppliance,
    },
    202: {
      bodyMapper: Mappers.VirtualNetworkAppliance,
    },
    204: {
      bodyMapper: Mappers.VirtualNetworkAppliance,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters86,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkApplianceName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const updateTagsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkAppliances/{virtualNetworkApplianceName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkAppliance,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters1,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.virtualNetworkApplianceName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const listAllOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Network/virtualNetworkAppliances",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkApplianceListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/virtualNetworkAppliances",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkApplianceListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.resourceGroupName, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAllNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkApplianceListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualNetworkApplianceListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
